import React, { useState } from 'react'
import styled from '@emotion/styled'
import { createPaymentMethod } from '../../services/GraphQlClientService'
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { useBooleanState, mq, trackPaymentInfoEntered } from '../../helpers'
import { gsColors } from '../../styles/Variables'
import { Header2, Text } from '../../styles/Components'
import { useUserContext } from '../../components/UserContext'

const NewPaymentMethodDiv = styled.div`
  ${props =>
    props.modal &&
    `
    padding: 0 30px 30px;
  `}
  text-align: center;
  display: flex;
  flex-direction: column;
  // gap: 48px;

  .brands {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 48px;

    ${mq('max', '768px')} {
      margin-top: 10px;
    }

    img {
      display: block;
      max-height: 22px;
      transition: opacity 250ms;

      &.inactive {
        opacity: 0.25;
      }
    }
  }

  .card-fields {
    margin-top: 48px;
    margin-bottom: 48px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;

    ${mq('max', '768px')} {
      margin-top: 10px;
      margin-bottom: 30px;
    }

    .card-field-row {
      display: flex;
      gap: 34px;
    }
  }

  .card-field-container {
    border-bottom: 1px solid ${gsColors.meredithGrey};
    width: 100%;
    padding: 12px 0;
  }

  .error {
    color: ${gsColors.sunsetOrange};
    font-size: 13px;
    margin: 10px 0 0;
    text-align: center;
  }
`

const SavePaymentButton = styled.button`
  font-weight: 600;
  padding: 14px 50px;
  width: auto;
  height: auto;
  color: black;
  background-color: transparent;
  border: 1px solid black;
  font-size: 18px;
  font-family: Euclid;
  
  &:disabled {
    color: #c7c7c7;
    border-color: ${gsColors.meredithGrey};
  }
`

const ELEMENT_STYLE = {
  base: {
    fontSize: '16px',
    color: gsColors.black,
    '::placeholder': {
      color: gsColors.meredithGrey
    },
  },
  invalid: {
    color: '#FC4945'
  }
}

const NewPaymentMethod = ({
  closeModal,
  createdPaymentMethodFn,
  modal = false,
  paymentMethods,
}) => {
  const { setUser, user: me } = useUserContext()
  const [cardErrorMessage, setCardErrorMessage] = useState('')
  const [working, setWorking] = useState(false)
  const [paymentMethodSaved, togglePaymentMethodSaved] = useBooleanState()

  const stripe = useStripe()
  const elements = useElements()

  // handleCardFieldChange: updates card status messages and brand on changes
  const handleCardFieldChange = ({ error }) => {
    if (error) {
      setCardErrorMessage(error.message)
    } else {
      setCardErrorMessage('')
    }
  }

  // savePaymentMethod: store card entry in Stripe and store token in starbase
  const savePaymentMethod = () => {
    return Promise.resolve(setWorking(true))
      .then(() => {
        const cardNumber = elements.getElement(CardNumberElement)
        return stripe.createToken(cardNumber)
      })
      .then(result => {
        if (result.error) {
          setCardErrorMessage(result.error.message)
          return false
        } else {
          return createPaymentMethod({
            userId: me.id,
            stripeTokenId: result.token.id
          })
        }
      })
      .then(paymentMethod => {
        if (paymentMethod.id) {
          trackPaymentInfoEntered()
          paymentMethods.push(paymentMethod)
          // call callback method for created payment
          createdPaymentMethodFn(paymentMethod)
          setUser({
            ...me,
            paymentMethods,
            defaultPaymentMethodId: paymentMethod.id
          })
          if (modal) {
            closeModal()
          } else {
            togglePaymentMethodSaved()
          }
        }
      })
      .catch(err => {
        console.error({ err })
        const message = err.message
        if (message) {
          setCardErrorMessage(
            message.indexOf('card_error') === 0
              ? message.replace(/card_error:\s/g, '')
              : 'There was an error saving your payment method.'
          )
        }
      })
      .finally(() => setWorking(false))
  }

  return (
    <NewPaymentMethodDiv {...{ modal }}>
      <Header2 fontSize={36} lineHeight='138%' spacing={0.36}>
        {modal ? 'Add a new payment method.' : 'Add your payment.'}
      </Header2>
      {
        elements &&
          <div className="card-fields">
            <div className="card-field-number card-field-container">
              <CardNumberElement
                options={{ style: ELEMENT_STYLE, placeholder: 'Card Number' }}
                onChange={handleCardFieldChange}
              />
            </div>
            <div className='card-field-row'>
              <div className="card-field-container">
                <CardExpiryElement
                  options={{ style: ELEMENT_STYLE }}
                  onChange={handleCardFieldChange}
                />
              </div>
              <div className="card-field-container">
                <CardCvcElement
                  options={{ style: ELEMENT_STYLE }}
                  onChange={handleCardFieldChange}
                />
              </div>
            </div>
          </div>
      }

      {cardErrorMessage && (
        <p className="error" role="alert">
          {cardErrorMessage}
        </p>
      )}
      <SavePaymentButton
        data-testid="save-payment-btn"
        onClick={savePaymentMethod}
        disabled={paymentMethodSaved || working}
        className="payment secondary"
        payment
        working={working}
      >
        {paymentMethodSaved ? 'Payment Saved' : 'Save Payment'}
      </SavePaymentButton>
      <ul className="brands">
        <li className="stripe">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src='/images/powered_by_stripe.svg' alt="Powered by Stripe" />
        </li>

        <Text color={gsColors.venus} fontSize={12}>
          Your personal information is protected
        </Text>
      </ul>

    </NewPaymentMethodDiv>
  )
}

export default NewPaymentMethod
